(() => {
  if (customElements.get('video-usp-gallery')) return;

  const DESKTOP_QUERY = '(min-width: 768px)';

  class VideoUspGallery extends HTMLElement {
    constructor() {
      super();
      this.onTriggerActivate = this.onTriggerActivate.bind(this);
      this.onTriggerHover = this.onTriggerHover.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.activeIndex = 0;
      this.isVisible = false;
    }

    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-usp-trigger]'));
      this.items = Array.from(this.querySelectorAll('[data-usp-item]'));
      this.mediaItems = Array.from(this.querySelectorAll('[data-usp-media]'));

      this.triggers.forEach((trigger) => {
        trigger.addEventListener('mouseenter', this.onTriggerHover);
        trigger.addEventListener('focus', this.onTriggerHover);
        trigger.addEventListener('click', this.onTriggerActivate);
      });

      this.setActive(0);

      this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
      this.observer.observe(this);

      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.triggers.forEach((trigger) => {
        trigger.removeEventListener('mouseenter', this.onTriggerHover);
        trigger.removeEventListener('focus', this.onTriggerHover);
        trigger.removeEventListener('click', this.onTriggerActivate);
      });
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    indexOf(trigger) {
      const index = parseInt(trigger.dataset.index, 10);
      return isNaN(index) ? 0 : index;
    }

    onTriggerHover(event) {
      if (!this.desktop.matches) return;
      this.activate(this.indexOf(event.currentTarget));
    }

    onTriggerActivate(event) {
      // Mobile: a tap only swaps the video and reveals the "Read more" link —
      // it never navigates. The visible "Read more" link handles navigation.
      if (!this.desktop.matches) {
        event.preventDefault();
        this.activate(this.indexOf(event.currentTarget));
        return;
      }

      // Desktop: items with a link navigate natively; otherwise just preview.
      this.activate(this.indexOf(event.currentTarget));
    }

    activate(index) {
      if (index !== this.activeIndex) this.setActive(index);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const trigger = event.target.querySelector('[data-usp-trigger]');
      if (trigger) this.activate(this.indexOf(trigger));
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        this.isVisible = entry.isIntersecting;
        if (entry.isIntersecting) this.warmInactiveVideos();

        const video = this.activeVideo();
        if (!video) return;
        if (entry.isIntersecting) {
          this.playVideo(video);
        } else {
          video.pause();
        }
      });
    }

    // Sources render with preload="none" so the page never pays for them up front.
    // Once the gallery is on screen, let the inactive ones fetch metadata so that
    // switching between items does not stall on connection setup.
    warmInactiveVideos() {
      if (this.warmed) return;
      this.warmed = true;

      this.mediaItems.forEach((media, i) => {
        if (i === this.activeIndex) return;
        const video = media.querySelector('video');
        if (video && video.preload === 'none') video.preload = 'metadata';
      });
    }

    activeVideo() {
      const media = this.mediaItems[this.activeIndex];
      return media ? media.querySelector('video') : null;
    }

    playVideo(video) {
      video.muted = true;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    }

    setActive(index) {
      this.activeIndex = index;

      this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
      this.triggers.forEach((trigger, i) => {
        if (i === index) {
          trigger.setAttribute('aria-current', 'true');
        } else {
          trigger.removeAttribute('aria-current');
        }
      });

      this.mediaItems.forEach((media, i) => {
        const isActive = i === index;
        media.classList.toggle('is-active', isActive);
        const video = media.querySelector('video');
        if (!video) return;
        if (isActive && this.isVisible) {
          this.playVideo(video);
        } else {
          video.pause();
        }
      });
    }
  }

  customElements.define('video-usp-gallery', VideoUspGallery);
})();
